import * as React from 'react';
import { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  EventBusy as EventBusyIcon,
  Apps as AppsIcon,
  Search as SearchIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  CalendarViewDay as CalendarViewDayIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { withTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../../utils/languages';
import { isSameDay, formatMonthTitle } from '../hooks/dateUtils';

function MobileCalendarHeader({
  t,
  i18n,
  selectedDate,
  onDateChange,
  onPrevDay,
  onNextDay,
  onToday,
  selectedResourceId,
  selectedResourceType,
  onResourceChange,
  employees,
  serviceBays,
  unscheduledCount,
  onOpenSearch,
  mode = 'day',
  onModeChange,
  monthCursor,
  monthWeeksText = '',
  onPrevMonth,
  onNextMonth,
}) {
  const isMonthMode = mode === 'month';
  const language = normalizeLanguage(i18n?.language);
  const [resourceMenuAnchor, setResourceMenuAnchor] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateTextRef = useRef(null);

  const today = new Date();
  const isToday = isMonthMode
    ? monthCursor && monthCursor.getFullYear() === today.getFullYear() && monthCursor.getMonth() === today.getMonth()
    : isSameDay(selectedDate, today);
  const resources = selectedResourceType === 'employee' ? employees : serviceBays;
  const selectedResource = resources.find((r) => r.id === selectedResourceId);

  const handleResourceMenuOpen = (event) => {
    setResourceMenuAnchor(event.currentTarget);
  };

  const handleResourceMenuClose = () => {
    setResourceMenuAnchor(null);
  };

  const handleResourceSelect = (resourceId, type) => {
    onResourceChange(resourceId, type);
    handleResourceMenuClose();
  };

  const formatDateDisplay = (date) => {
    const d = new Date(date);
    // Get weekday name in Finnish
    const dayName = d.toLocaleDateString('fi-FI', { weekday: 'long' });
    // Capitalize first letter
    const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    // Get date in Finnish format (d.M.yyyy)
    const dateStr = d.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
    return { dayName: capitalizedDayName, dateStr };
  };

  const { dayName, dateStr } = formatDateDisplay(selectedDate);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
    >
      {/* Date Navigation Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 1,
        }}
      >
        {/* Balances the two buttons on the right so the date stays centred */}
        <Box sx={{ width: 68, flexShrink: 0 }} />

        <IconButton onClick={isMonthMode ? onPrevMonth : onPrevDay} size="small" data-testid="mobile-calendar-prev">
          <ChevronLeftIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <Box
            ref={dateTextRef}
            onClick={() => setDatePickerOpen(true)}
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: '8px',
              px: 1,
              py: 0.5,
              '&:hover': { bgcolor: 'action.hover' },
              transition: 'background-color 0.15s',
              userSelect: 'none',
            }}
          >
            <Typography
              variant="subtitle1"
              data-testid="mobile-calendar-title"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                color: isToday ? 'primary.main' : 'text.primary',
              }}
            >
              {isMonthMode ? formatMonthTitle(monthCursor || selectedDate, language) : dayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMonthMode ? (monthWeeksText ? `${t('calendar.month.weeks')} ${monthWeeksText}` : '\u00a0') : dateStr}
            </Typography>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              open={datePickerOpen}
              onOpen={() => setDatePickerOpen(true)}
              onClose={() => setDatePickerOpen(false)}
              value={selectedDate}
              onChange={(newDate) => {
                if (newDate && !isNaN(newDate.getTime())) {
                  onDateChange(newDate);
                }
                setDatePickerOpen(false);
              }}
              slotProps={{
                textField: { sx: { display: 'none' } },
                popper: { anchorEl: dateTextRef.current },
              }}
            />
          </LocalizationProvider>

          {!isToday && (
            <IconButton onClick={onToday} size="small" color="primary">
              <TodayIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <IconButton onClick={isMonthMode ? onNextMonth : onNextDay} size="small" data-testid="mobile-calendar-next">
          <ChevronRightIcon />
        </IconButton>

        {/* Day / month switch: one icon, so the header does not grow another row */}
        <IconButton
          onClick={() => onModeChange && onModeChange(isMonthMode ? 'day' : 'month')}
          size="small"
          data-testid="mobile-calendar-mode-toggle"
          data-mode={mode}
          aria-label={isMonthMode ? t('Day view') : t('Month')}
          color={isMonthMode ? 'primary' : 'default'}
          sx={{ flexShrink: 0 }}
        >
          {isMonthMode ? <CalendarViewDayIcon /> : <CalendarViewMonthIcon />}
        </IconButton>

        <IconButton
          onClick={onOpenSearch}
          size="small"
          data-testid="mobile-calendar-search-button"
          aria-label={t('Search work orders')}
          sx={{ flexShrink: 0 }}
        >
          <SearchIcon />
        </IconButton>
      </Box>

      {/* Resource Selector Row (the month grid is always the whole workshop) */}
      {!isMonthMode && <Box
        sx={{
          display: 'flex',
          px: 1.5,
          pb: 1,
          gap: 0.75,
        }}
      >
        <Chip
          icon={<AppsIcon sx={{ fontSize: 16 }} />}
          label={t('All')}
          variant={selectedResourceType === 'all' ? 'filled' : 'outlined'}
          color={selectedResourceType === 'all' ? 'primary' : 'default'}
          onClick={() => onResourceChange(null, 'all')}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 40,
            fontSize: '13px',
            '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        />
        <Chip
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          label={t('Employees')}
          variant={selectedResourceType === 'employee' ? 'filled' : 'outlined'}
          color={selectedResourceType === 'employee' ? 'primary' : 'default'}
          onClick={() => {
            if (selectedResourceType !== 'employee') {
              onResourceChange(null, 'employee');
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 40,
            fontSize: '13px',
            '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        />
        <Chip
          icon={<BuildIcon sx={{ fontSize: 16 }} />}
          label={t('Service bays')}
          variant={selectedResourceType === 'bay' ? 'filled' : 'outlined'}
          color={selectedResourceType === 'bay' ? 'primary' : 'default'}
          onClick={() => {
            if (selectedResourceType !== 'bay') {
              onResourceChange(null, 'bay');
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 40,
            fontSize: '13px',
            '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        />
      </Box>}

      {/* Selected Resource Dropdown - shown for employee and bay modes */}
      {!isMonthMode && selectedResourceType !== 'all' && (
        <Box
          sx={{
            px: 1.5,
            pb: 1,
          }}
        >
          <Chip
            label={selectedResource?.name || t('All')}
            onClick={handleResourceMenuOpen}
            onDelete={handleResourceMenuOpen}
            deleteIcon={<ExpandMoreIcon />}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              bgcolor: 'grey.100',
              '& .MuiChip-label': {
                flex: 1,
                textAlign: 'left',
              },
            }}
          />

          <Menu
            anchorEl={resourceMenuAnchor}
            open={Boolean(resourceMenuAnchor)}
            onClose={handleResourceMenuClose}
            PaperProps={{
              sx: {
                maxHeight: 300,
                minWidth: 200,
              },
            }}
          >
            <MenuItem
              selected={!selectedResourceId}
              onClick={() => handleResourceSelect(null, selectedResourceType)}
            >
              <ListItemIcon>
                <AppsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('All')} />
            </MenuItem>
            {selectedResourceType === 'employee' && employees.length > 0 &&
              employees.map((employee) => (
                <MenuItem
                  key={employee.id}
                  selected={employee.id === selectedResourceId}
                  onClick={() => handleResourceSelect(employee.id, 'employee')}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={employee.name} />
                </MenuItem>
              ))
            }
            {selectedResourceType === 'bay' && serviceBays.length > 0 &&
              serviceBays.map((bay) => (
                <MenuItem
                  key={bay.id}
                  selected={bay.id === selectedResourceId}
                  onClick={() => handleResourceSelect(bay.id, 'bay')}
                >
                  <ListItemIcon>
                    <BuildIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={bay.name} />
                </MenuItem>
              ))
            }
          </Menu>
        </Box>
      )}
    </Box>
  );
}

export default withTranslation()(MobileCalendarHeader);
